import { inject, injectable, injectFromBase } from 'inversify'
import DomainBaseService from '@/services/domain/domainBaseService'
import { DomainSettings } from '@/decorators/domainDecorators'
import GettingStartedInfo from '@/data/models/domain/GettingStartedInfo'
import type GettingStartedInfoRepository from '@/data/repositories/domain/gettingStartedInfoRepository'
import type EntityManagerService from '@/data/repositories/entityManagerService'
import type ModelSubscriptionService from '@/services/transport/modelSubscriptionService'
import type PreloaderManager from '@/services/preloaders/preloaderManager'
import { SERVICE_TYPES } from '@/core/container/types'
import type UserService from '@/services/domain/user/userService'
import type DateTimeService from '@/services/dateTimeService'

@DomainSettings({
  model: GettingStartedInfo,
})
@injectable()
@injectFromBase()
export default class GettingStartedInfoService extends DomainBaseService<GettingStartedInfoRepository> {
  constructor(
    @inject(SERVICE_TYPES.EntityManager) protected readonly entityManager: EntityManagerService,
    @inject(SERVICE_TYPES.ModelSubscriptionService) protected readonly subscription: ModelSubscriptionService,
    @inject(SERVICE_TYPES.PreloaderManager) protected readonly preloaderManager: PreloaderManager,
    @inject(SERVICE_TYPES.UserService) protected readonly userService: UserService,
    @inject(SERVICE_TYPES.DateTimeService) protected readonly dateTimeService: DateTimeService,
  ) {
    super(entityManager, subscription, preloaderManager)
  }

  public getInfo() {
    return this.getDomainRepository().getInfo()
  }

  public findInfo() {
    return this.getDomainRepository().findInfo()
  }

  public findInfoOrFail() {
    return this.getDomainRepository().findInfoOrFail()
  }

  public canGetFreeNumber() {
    const createdAt = this.userService.currentUser().createdAt

    const registeredAt = new Date(createdAt)
    const sixMonthsLater = this.dateTimeService.addMonths(registeredAt, 6)

    return !this.dateTimeService.isAfter(Date.now(), sixMonthsLater)
  }

  public async findOrFillInfo() {
    let info = this.findInfo()
    if (!info) {
      info = await this.getInfo()
    }
    return info
  }
}
